import { Button, Checkbox } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import "../translations.js";
// The fnl-* rules live with the add-link modal, which was the original home of
// this markup. Imported here so the section carries its own styling wherever it
// is mounted (CRA CSS is global; importing twice is a no-op).
import "./addWebsiteLinkModal.css";

/**
 * One collapsible bucket of discovered links (new / already trained / gone).
 *
 * Extracted verbatim from addWebsiteLinkModal so the add-link crawl tab and the
 * "Check for updates" dialog show the identical list instead of two drifting
 * copies. The two differ only in the header control:
 *   - `action`        renders the per-bucket action button (add-link modal)
 *   - `headerCheckbox` renders a group opt-in checkbox (update-check dialog,
 *                      where one confirm button at the bottom applies everything)
 */
const LinkBucketSection = ({
  icon,
  iconClassName = "links-status-icon",
  title,
  links = [],
  selected = [],
  expanded = false,
  onToggleExpand,
  onToggleLink,
  onSelectAll,
  action = null,
  headerCheckbox = null,
  showSelectedCount = true,
}) => {
  const { t } = useTranslation();
  const hasData = links.length > 0;
  const allSelected = hasData && selected.length === links.length;

  return (
    <div className="fnl-section">
      <div
        className={`fnl-exp-header${hasData ? "" : " disabled"}`}
        onClick={() => onToggleExpand?.(hasData)}
        title={hasData ? undefined : t("no_data_to_show")}
      >
        <div className="fnl-exp-left">
          {headerCheckbox && (
            <Checkbox
              checked={headerCheckbox.checked}
              indeterminate={headerCheckbox.indeterminate}
              disabled={headerCheckbox.disabled || !hasData}
              onClick={(e) => e.stopPropagation()}
              onChange={headerCheckbox.onChange}
              sx={{ color: "#5616ea", "&.Mui-checked": { color: "#5616ea" } }}
              className="row-checkbox"
            />
          )}
          <FontAwesomeIcon
            className={`fnl-chevron${hasData ? "" : " disabled"}`}
            icon={expanded ? faChevronDown : faChevronRight}
          />
          <div className="fnl-exp-title">
            <FontAwesomeIcon className={`${iconClassName}${hasData ? "" : " disabled"}`} icon={icon} />
            {`${title} (${links.length})`}
          </div>
        </div>
        {showSelectedCount && (
          <div className="fnl-selected-count">{t("n_selected", { count: selected.length })}</div>
        )}
        {action && (
          <Button
            onClick={(e) => { e.stopPropagation(); action.onClick(); }}
            variant="contained"
            className="fnl-inline-action"
            disabled={selected.length === 0}
            sx={{
              backgroundColor: "#5616ea",
              "&:hover": { backgroundColor: "#4a14d6" },
              "&.Mui-disabled": { backgroundColor: "#cfc7ff" },
              padding: "6px 8px !important",
              minWidth: "auto",
              width: 85,
              fontSize: "14px !important",
              lineHeight: 1,
            }}
          >
            {action.label}
          </Button>
        )}
      </div>

      {expanded && hasData && (
        <>
          {/* With a group checkbox in the header, a second select-all here would
              be the same control twice. */}
          {!headerCheckbox && (
            <label className="fnl-checkbox-header">
              <Checkbox
                checked={allSelected}
                indeterminate={selected.length > 0 && selected.length < links.length}
                onChange={onSelectAll}
                sx={{ color: "#5616ea", "&.Mui-checked": { color: "#5616ea" } }}
                className="row-checkbox"
              />
              {t("select_all")}
            </label>
          )}
          <div className="fnl-link-box">
            {links.map((l) => (
              <label key={l} className="fnl-checkbox-row">
                <Checkbox
                  checked={selected.includes(l)}
                  onChange={onToggleLink(l)}
                  sx={{ color: "#5616ea", "&.Mui-checked": { color: "#5616ea" } }}
                  className="row-checkbox"
                />
                {l}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LinkBucketSection;
